import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Phone, MapPin, Clock, Navigation, CheckCircle, ChevronRight } from 'lucide-react';
import { BUSINESS, NEIGHBORHOODS, RECENT_REPAIRS } from '@/lib/businessData';
import Seo from '@/components/Seo';

const NEIGHBORHOOD_DETAILS = [
  { name: 'Bridgeland', zip: '77433', desc: 'Master-planned community in northwest Cypress. New construction homes with modern garage doors — we handle opener installs, smart-home programming, and spring replacements across all phases.', travel: '12 min from shop' },
  { name: 'Towne Lake', zip: '77433', desc: 'Waterfront community with custom homes and premium garage doors. We service carriage-house doors, insulated steel doors, and LiftMaster smart openers throughout Towne Lake.', travel: '15 min from shop' },
  { name: 'Cypress Creek', zip: '77429', desc: 'Established neighborhood near our shop. Quick response for spring snaps, off-track doors, and sensor realignment — often under 30 minutes.', travel: '5 min from shop' },
  { name: 'Coles Crossing', zip: '77429', desc: 'Family-friendly community off Hwy 290. We\'ve serviced hundreds of homes here — from roller replacements to full door installations.', travel: '8 min from shop' },
  { name: 'Fairfield', zip: '77433', desc: 'Large master-planned community with active families. Frequent calls for opener repairs, cable replacements, and safety sensor fixes.', travel: '14 min from shop' },
  { name: 'Cypress Lakes', zip: '77433', desc: 'Golf course community with custom homes. We install premium doors and handle complex torsion spring systems on heavier custom doors.', travel: '16 min from shop' },
  { name: 'Blackhorse Ranch', zip: '77449', desc: 'Gated community with larger homes and heavier doors. Our technicians carry heavy-duty spring gauges for these doors specifically.', travel: '18 min from shop' },
  { name: 'Stonegate', zip: '77429', desc: 'Quiet residential area near Cypress Creek HS. Same-day service for all garage door repair needs, including emergency off-track situations.', travel: '10 min from shop' },
];

const NEARBY_CITIES = [
  { name: 'Katy', distance: '20 min', desc: 'Garage door repair in Katy, TX' },
  { name: 'Spring', distance: '25 min', desc: 'Garage door repair in Spring, TX' },
  { name: 'Tomball', distance: '22 min', desc: 'Garage door repair in Tomball, TX' },
  { name: 'Houston', distance: '35 min', desc: 'Garage door repair in Northwest Houston' },
  { name: 'Jersey Village', distance: '15 min', desc: 'Garage door repair in Jersey Village, TX' },
  { name: 'Waller', distance: '30 min', desc: 'Garage door repair in Waller, TX' },
];

export default function ServiceAreas() {
  return (
    <>
      <Seo
        title="Garage Door Repair Service Areas | Cypress, TX Neighborhoods"
        description="Mike's Garage Door Repair serves every Cypress, TX neighborhood — Bridgeland, Towne Lake, Coles Crossing, Fairfield, and more — plus Katy, Spring, and Tomball. 30-minute average response."
        path="/service-areas"
      />
      <Navbar />
      <main className="pt-16">
        {/* Hero */}
        <section className="bg-background border-b border-border tech-grid">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <div className="max-w-3xl">
              <div className="font-mono text-xs text-primary uppercase tracking-wider stencil mb-4">
                / Service Areas — Cypress TX
              </div>
              <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-foreground text-balance leading-[0.95]">
                Garage Door Repair <span className="text-primary">Near You</span> in Cypress, TX
              </h1>
              <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
                Mike's Garage Door Repair serves every neighborhood in Cypress, Texas — from Bridgeland to
                Blackhorse Ranch. Our shop on Cypress Rosehill Rd puts us minutes from your door, with an
                average response time of 30 minutes for emergency calls.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a href={`tel:${BUSINESS.phoneRaw}`} className="flex items-center justify-center gap-2 bg-primary text-background px-6 py-4 font-heading font-bold text-sm uppercase tracking-wider hover:amber-glow transition-all">
                  <Phone className="w-5 h-5" />
                  {BUSINESS.phone}
                </a>
                <a href="/#booking" className="flex items-center justify-center gap-2 border-2 border-border text-foreground px-6 py-4 font-heading font-bold text-sm uppercase tracking-wider hover:border-primary hover:text-primary transition-all">
                  Book Online
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Coverage stats */}
        <section className="bg-card border-b border-border py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { value: '30 min', label: 'Avg Response Time' },
                { value: NEIGHBORHOODS.length, label: 'Cypress Neighborhoods' },
                { value: '6+', label: 'Nearby Cities' },
                { value: '24/7', label: 'Emergency Coverage' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="font-heading font-black text-3xl lg:text-4xl text-primary">{stat.value}</div>
                  <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Neighborhood grid */}
        <section className="bg-background py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-12">
              <div className="font-mono text-xs text-primary uppercase tracking-wider stencil mb-3">/ Cypress Neighborhoods</div>
              <h2 className="font-heading font-black text-3xl sm:text-4xl text-foreground text-balance">
                Every Cypress Community, Covered
              </h2>
              <p className="mt-4 text-lg text-muted-foreground max-w-2xl">
                Click your neighborhood to learn about the specific garage door services we provide in your area.
                All neighborhoods listed are within our 30-minute emergency response zone.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {NEIGHBORHOOD_DETAILS.map((hood) => (
                <div key={hood.name} className="bg-card border border-border p-5 hover:border-primary/50 transition-all group">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-heading font-bold text-base text-foreground group-hover:text-primary transition-colors">{hood.name}</h3>
                      <div className="font-mono text-[10px] text-muted-foreground">Cypress, TX {hood.zip}</div>
                    </div>
                    <MapPin className="w-4 h-4 text-primary shrink-0" />
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-3">{hood.desc}</p>
                  <div className="flex items-center gap-1 font-mono text-[10px] text-primary">
                    <Navigation className="w-3 h-3" />
                    {hood.travel}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Nearby cities */}
        <section className="bg-card border-y border-border py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10">
              <div className="font-mono text-xs text-primary uppercase tracking-wider stencil mb-3">/ Beyond Cypress</div>
              <h2 className="font-heading font-black text-3xl sm:text-4xl text-foreground text-balance">
                Also Serving Greater Northwest Houston
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {NEARBY_CITIES.map((city) => (
                <div key={city.name} className="bg-background border border-border p-4 text-center hover:border-primary/50 transition-all">
                  <div className="font-heading font-bold text-base text-foreground">{city.name}</div>
                  <div className="font-mono text-[10px] text-primary mt-1">{city.distance}</div>
                  <div className="font-mono text-[9px] text-muted-foreground mt-1">{city.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Recent repairs live feed */}
        <section className="bg-background py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-3 h-3 bg-primary rounded-full pulse-amber" />
              <div className="font-mono text-xs text-primary uppercase tracking-wider stencil">/ Live Repair Feed — Today</div>
            </div>
            <div className="bg-card border border-border divide-y divide-border">
              {RECENT_REPAIRS.map((repair, idx) => (
                <div key={idx} className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <CheckCircle className="w-4 h-4 text-primary shrink-0" />
                    <div className="min-w-0">
                      <div className="font-heading font-bold text-sm text-foreground truncate">{repair.service}</div>
                      <div className="font-mono text-[10px] text-muted-foreground">{repair.neighborhood}, Cypress TX</div>
                    </div>
                  </div>
                  <div className="font-mono text-[10px] text-muted-foreground shrink-0 ml-4">{repair.time}</div>
                </div>
              ))}
            </div>
            <p className="mt-4 font-mono text-[10px] text-muted-foreground">
              Live data from our dispatch system. These are real jobs completed by Mike's team across Cypress today.
            </p>
          </div>
        </section>

        {/* SEO content block */}
        <section className="bg-card border-t border-border py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading font-bold text-2xl text-foreground mb-4">
              Garage Door Repair Cypress TX — Full Coverage Map
            </h2>
            <div className="font-mono text-xs text-muted-foreground/80 leading-relaxed space-y-3">
              <p>
                <strong className="text-muted-foreground">Mike's Garage Door Repair</strong> provides 24/7 emergency
                garage door repair and installation throughout Cypress, Texas and the surrounding northwest Houston
                area. Our shop is centrally located on Cypress Rosehill Rd (FM 529), putting us within minutes of
                every major Cypress neighborhood including Bridgeland, Towne Lake, Cypress Creek Ranch, Coles
                Crossing, Fairfield, Cypress Lakes, Blackhorse Ranch, and Stonegate.
              </p>
              <p>
                Whether you're searching for "garage door repair near me" in Bridgeland, need a torsion spring
                replaced in Towne Lake, or have an off-track door in Fairfield, our technicians arrive fast with
                all parts on the truck. We also serve Katy, Spring, Tomball, Jersey Village, Waller, and Northwest
                Houston. Licensed ({BUSINESS.license}), insured, and locally owned for over {BUSINESS.yearsInBusiness} years.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-primary text-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-heading font-black text-2xl sm:text-3xl">In Your Neighborhood? We're On The Way.</h2>
              <p className="font-mono text-sm opacity-80 mt-1">30-minute average response across all of Cypress, TX.</p>
            </div>
            <a href={`tel:${BUSINESS.phoneRaw}`} className="flex items-center gap-2 bg-background text-primary px-6 py-4 font-heading font-bold text-sm uppercase tracking-wider hover:opacity-90 transition-opacity">
              <Phone className="w-5 h-5" />
              {BUSINESS.phone}
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}